import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useBridgeContext } from '@sendible/shared-state-bridge';
import { useReportingContext, buildGridLayoutFromModules } from '../../../context';
import { useGetReport, useGetTemplates, useGetProfiles, useGetModuleDefinitions } from '../../../../../models/reporting';
import { normalisePlatform } from '../../../../../models/reporting/transforms';
import { resolveProfiles, getDateISO } from '../../../helpers';

// ── Types ──
interface ReportPeriod {
  startDate: string;
  endDate: string;
}

// ── Helpers ──
const buildDefaultPeriod = (timezoneMinutes: number): ReportPeriod => ({
  startDate: getDateISO(timezoneMinutes, 7),
  endDate: getDateISO(timezoneMinutes, 1),
});

// ── Hook ──
export const useReportInitialisation = () => {
  const { t } = useTranslation('reports');
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [{ user }] = useBridgeContext();

  const {
    setReportId,
    setProfiles,
    setModules,
    modules,
    profiles,
    updateGridLayout,
    snapshotState,
    setReportName: setContextReportName,
    setPeriod,
    setIsLoadedFromTemplate,
  } = useReportingContext();

  const isNewReport = id === 'new';
  const timezone = user.timezone ?? 0;

  const { data: apiReport, isLoading: isLoadingReport, isError: isReportError } = useGetReport(isNewReport ? null : (id ?? null));
  const { data: apiTemplates } = useGetTemplates();
  const { data: allProfiles, isLoading: isLoadingProfiles } = useGetProfiles();
  const { data: moduleDefinitions } = useGetModuleDefinitions();

  const report = isNewReport ? null : (apiReport ?? null);

  // ── Initialisation Tracking (States & Refs) ──
  const [lastInitialisedId, setLastInitialisedId] = useState<string | null>(null);
  const [isOpenedFromTemplate, setIsOpenedFromTemplate] = useState(false);

  const profilesResolvedForId = useRef<string | null>(null);
  const hasSnapshotted = useRef<string | null>(null);
  const latestSnapshotState = useRef(snapshotState);

  // Keep the snapshot function reference fresh
  useEffect(() => {
    latestSnapshotState.current = snapshotState;
  }, [snapshotState]);

  // ── Phase 1: Render-Phase Sync (Updates Context State During Render) ──
  const targetId = isNewReport ? 'new' : (report?.id ?? null);

  if (targetId !== null && lastInitialisedId !== targetId) {
    const defaultPeriod = buildDefaultPeriod(timezone);

    if (isNewReport) {
      const templateId = searchParams.get('template');
      const templates = apiTemplates ?? [];

      // Ensure dependencies are loaded before committing to 'new' report state setup
      if (!templateId || (apiTemplates !== undefined && moduleDefinitions && !isLoadingProfiles)) {
        setLastInitialisedId('new');

        const template = templateId ? templates.find((tmpl) => tmpl.id === templateId) : null;

        if (template && template.modules.length > 0) {
          setIsOpenedFromTemplate(true);
          setIsLoadedFromTemplate(true);

          const templateModules = template.modules.map((m) => ({ ...m, reportModuleId: `rm_${uuidv4()}` }));

          setReportId(`new_${uuidv4()}`);
          setProfiles(allProfiles?.filter((p) => normalisePlatform(p.platform) === template.platformType) ?? []);
          setModules(templateModules);
          setContextReportName(template.name);
          setPeriod(defaultPeriod);
          updateGridLayout(buildGridLayoutFromModules(templateModules, moduleDefinitions));
        } else {
          setIsOpenedFromTemplate(false);
          setIsLoadedFromTemplate(false);
          setReportId(`new_${uuidv4()}`);
          setProfiles([]);
          setModules([]);
          setPeriod(defaultPeriod);
          updateGridLayout([]);
          setContextReportName(t('new_reports_untitled_report') as string);
        }
      }
    } else if (report) {
      setLastInitialisedId(report.id);
      setIsOpenedFromTemplate(false);
      setIsLoadedFromTemplate(false);
      setReportId(report.id);
      setModules(report.modules ?? []);
      setContextReportName(report.name);
      setPeriod(defaultPeriod);
      updateGridLayout(buildGridLayoutFromModules(report.modules ?? [], moduleDefinitions));
    }
  }

  // ── Phase 2: Resolve profileIds → full ReportProfile objects ──
  useEffect(() => {
    if (isNewReport || !report || isLoadingProfiles) return;

    const resolveKey = `${report.id}:${user.userId}`;

    if (profilesResolvedForId.current === resolveKey) return;
    profilesResolvedForId.current = resolveKey;

    setProfiles(resolveProfiles(report.profileIds ?? [], allProfiles));

    // Reset snapshot tracking so the snapshotter re-fires once profiles populate
    hasSnapshotted.current = null;
  }, [report, allProfiles, isNewReport, isLoadingProfiles, setProfiles, user.userId]);

  // ── Phase 3: Delayed Snapshotter ──
  useEffect(() => {
    if (!lastInitialisedId || isOpenedFromTemplate || hasSnapshotted.current === lastInitialisedId) return;

    // Separate state adjustment from execution pass using requestAnimationFrame
    requestAnimationFrame(() => {
      hasSnapshotted.current = lastInitialisedId;
      latestSnapshotState.current();
    });
  }, [modules, profiles, lastInitialisedId, isOpenedFromTemplate]);

  return {
    isNewReport,
    isLoadingReport,
    isReportError,
    report,
    allProfiles,
    moduleDefinitions,
    hasSnapshotted,
    latestSnapshotState,
    isOpenedFromTemplate,
  };
};
